/**
 * Reel-strip render decisions for the full-page anchor editor — extracted as pure functions per
 * this repo's convention (no React Testing Library; render decisions live in testable functions,
 * e.g. `checkOrder.ts`/`videoLoad.ts`).
 */

export type AnchorCardBorderState = "pending" | "resolved" | "outOfOrder";

/** Which border/tone a reel card (or the stage's own out-of-order banner) reads as: plain/pending
 *  until resolved (green), and out-of-order always wins as a full-perimeter danger state
 *  regardless of resolved state. */
export function anchorCardBorderState(
  outOfOrder: boolean,
  resolved: boolean
): AnchorCardBorderState {
  if (outOfOrder) {
    return "outOfOrder";
  }
  return resolved ? "resolved" : "pending";
}

export interface ReelCardVisual {
  tone: AnchorCardBorderState;
  /** The single "current" card — larger, amber ring (Option A mockup's `.clip.now`). Independent
   *  of `tone`: the current card can simultaneously be out-of-order (re-opening an already-
   *  resolved, out-of-order anchor via a reel-card click) — both facts render at once, they don't
   *  override each other. */
  focused: boolean;
}

/** Combines the two independent reel-card decisions (border tone, focused/current) into the one
 *  shape `ReelStrip.tsx` renders from — a single tested function rather than each card re-deriving
 *  both itself. */
export function reelCardVisual(
  anchorId: number,
  currentAnchorId: number,
  resolved: boolean,
  outOfOrder: boolean
): ReelCardVisual {
  return {
    tone: anchorCardBorderState(outOfOrder, resolved),
    focused: anchorId === currentAnchorId,
  };
}

/** A reel card's clip-identity label — `anchor.clipName` (the camera clip's own filename, e.g.
 *  `"A012_C0031"`) when n8n sent one, else a defined fallback rather than an empty/undefined
 *  string reaching the DOM: an older stored anchor row written before `clipName` existed on the
 *  wire falls back to `"Anchor <n>"` so the card still reads as something. */
export function reelCardLabel(
  anchorId: number,
  clipName: string | undefined
): string {
  return clipName && clipName.trim() ? clipName : `Anchor ${anchorId}`;
}
